import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useStaffWorkspace } from "../../../app/session";
import { Icon, type IconName } from "../../../components/Icon";
import { CreatorOfficeLogo } from "../../../components/shell/Logo";
import { t } from "../../../lib/sprache";
import { EINST, arbeitsseite, gruppeTitel, kartenGruppen, suchbareKarten, sucheEinstellungen, type GruppeKey, type Karte, type Sicht, type Treffer } from "./baum";
import { upgradeText } from "../../../model/tarif";
import { ANLEITUNG_VIDEO_URL } from "../../../app/nav";
import { useAnkerSprung, useKartenSperre } from "./parts";
import "./einstellungen.css";
import "./einstellungen-vollbild.css";

/**
 * „Alle Einstellungen“ (IA-1, Inhaber 21.10.2026, Zoho-Vorbild): Startseite im Vollbild mit den Karten-Gruppen. Die Kopfzeile
 * (EinstellungenKopf) ersetzt im Vollbild die Arbeitskopfzeile: Titel, Suche (Taste /) und „Einstellungen schließen ✕“ zurück
 * zur letzten Arbeitsseite. Am Handy ist die Kartenliste die erste Ebene, jede Unterseite hat oben „‹ Alle Einstellungen“.
 */

const GRUPPEN_ICON: Record<GruppeKey, IconName> = {
  agentur: "file", team: "users", steuern: "receipt", geld: "wallet", anpassung: "edit", module: "grid", verbindungen: "link", konto: "card", persoenlich: "user",
};

export function AlleEinstellungen() {
  const ws = useStaffWorkspace();
  useAnkerSprung();
  const { geladen, sperre } = useKartenSperre();
  const sicht: Sicht = { chef: ws.isChef, creator: !!ws.isCreator };
  // Ohne geladene Rolle und Tarifrechte startet die Liste leer (Ladezustand), statt kurz alle Karten zu zeigen
  const gruppen = geladen ? kartenGruppen(sicht) : [];
  return (
    <main id="main" className="content tk-alle-einst">
      <h1 className="sr-only">{t("Alle Einstellungen", "All settings")}</h1>
      {!geladen && <p className="muted tk-alle-laden" role="status" aria-busy="true">{t("Einstellungen werden geladen …", "Loading settings …")}</p>}
      {geladen && !ws.isChef && (
        <p className="notice">{t("Einstellungen der Agentur, Team und Abo verwaltet der Admin deiner Agentur. Hier stehen deine persönlichen Einstellungen.", "Agency settings, team and subscription are managed by your agency's admin. Here are your personal settings.")}</p>
      )}
      {/* Ausführliche Anleitung (03.11.2026): Video zu allen Einstellungen auf der Website, neuer Tab; nicht im Creator-Konto */}
      {geladen && !ws.isCreator && (
        <p className="tk-alle-anleitung">
          <span className="tk-alle-icon" aria-hidden="true"><Icon name="play" /></span>
          <a href={ANLEITUNG_VIDEO_URL} target="_blank" rel="noreferrer">{t("Ausführliche Anleitung (14 Min.)", "Detailed guide (14 min.)")}</a>
          <small className="muted">{t("Video zu allen Einstellungen: was sie bewirken und wie du sie setzt", "Video on all settings: what they do and how to set them")}</small>
        </p>
      )}
      <div className="tk-alle-gruppen">
        {gruppen.map(({ gruppe, karten }) => (
          <section key={gruppe.key} id={gruppe.key} className="tk-alle-gruppe" aria-labelledby={`alle-gruppe-${gruppe.key}`}>
            <h2 id={`alle-gruppe-${gruppe.key}`}>{gruppe.titel()}</h2>
            <ul>
              {karten.map((k) => (
                <li key={k.key}><KartenLink k={k} zu={sperre(k)} icon={GRUPPEN_ICON[gruppe.key]} /></li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </main>
  );
}

/** Karte: Seite öffnen; im Tarif gesperrt mit Etikett „ab Team“ bzw. „ab Creator Pro“ und Weg zum Upgrade statt zur Seite. */
function KartenLink({ k, zu, icon }: { k: Karte; zu: { text: string; tarif: string; to: string } | null; icon: IconName }) {
  const titel = k.titel();
  // Kurzbeschreibung aus den Abschnitten der Seite (Stripe-Vorbild): bis zu zwei Begriffe außer dem Titel selbst
  const text = k.begriffe().filter((b) => b.toLowerCase() !== titel.toLowerCase()).slice(0, 2).join(", ");
  return (
    <Link className={`tk-alle-link${zu ? " tk-alle-gesperrt" : ""}`} to={zu ? zu.to : k.to} title={zu ? upgradeText(zu.tarif) : undefined}>
      <span className="tk-alle-icon" aria-hidden="true"><Icon name={icon} /></span>
      <span className="tk-alle-text">
        <span className="tk-alle-titel">{titel}{zu && <span className="pill tk-einst-etikett">{zu.text}</span>}</span>
        {text && <span className="tk-alle-beschr">{text}</span>}
      </span>
    </Link>
  );
}

/** Kopfzeile im Vollbild (Agentur- und Studio-Rahmen). */
export function EinstellungenKopf({ orgName, sicht }: { orgName: string; sicht: Sicht }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const start = pathname.replace(/\/+$/, "") === EINST;
  return (
    <div className="tk-voll-kopf-inhalt">
      <div className="tk-voll-titel">
        {/* Regel Marke 29.09.2026: oben links immer das CreatorOffice-Logo (hier das Zeichen, wie bei Zoho das App-Symbol) */}
        <Link className="brand co-brand tk-voll-logo" to="/agentur"><CreatorOfficeLogo form="symbol" /></Link>
        {!start && <Link className="btn tk-voll-zurueck" to={EINST} aria-label={t("Zurück zu allen Einstellungen", "Back to all settings")}>‹</Link>}
        <div className="tk-voll-name">
          <strong>{t("Alle Einstellungen", "All settings")}</strong>
          <small>{orgName}</small>
        </div>
      </div>
      <EinstellungenSuche sicht={sicht} />
      <button type="button" className="tk-voll-schliessen" onClick={() => navigate(arbeitsseite())} aria-label={t("Einstellungen schließen", "Close settings")}>
        <span className="tk-voll-schliessen-text">{t("Einstellungen schließen", "Close settings")}</span>
        <span aria-hidden="true">✕</span>
      </button>
    </div>
  );
}

/**
 * Suche über Kartentitel, Abschnittsüberschriften, Feldbeschriftungen und Synonyme; ein Treffer springt per Anker in den
 * Abschnitt. Taste „/“ setzt den Fokus ins Feld (außer beim Tippen in einem anderen Feld).
 */
export function EinstellungenSuche({ sicht }: { sicht: Sicht }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [aktiv, setAktiv] = useState(0);
  const [offen, setOffen] = useState(false);
  const feld = useRef<HTMLInputElement>(null);
  const listeId = useId();
  const { sperre } = useKartenSperre();
  const treffer: Treffer[] = q.trim() ? sucheEinstellungen(q, suchbareKarten(sicht)) : [];

  useEffect(() => {
    const taste = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const ziel = e.target as HTMLElement | null;
      if (ziel && (ziel.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(ziel.tagName))) return;
      e.preventDefault();
      feld.current?.focus();
    };
    document.addEventListener("keydown", taste);
    return () => document.removeEventListener("keydown", taste);
  }, []);

  const waehle = (x: Treffer) => {
    setQ("");
    setOffen(false);
    feld.current?.blur();
    // Im Tarif gesperrt: nicht in die Seite, sondern zum Upgrade (das Etikett steht im Treffer)
    navigate(sperre(x.karte)?.to ?? x.karte.to);
  };
  const tasten = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOffen(true); setAktiv((a) => Math.min(a + 1, treffer.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setAktiv((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { const x = treffer[aktiv]; if (x) { e.preventDefault(); waehle(x); } }
    else if (e.key === "Escape") { if (q) { setQ(""); } else feld.current?.blur(); setOffen(false); }
  };
  const zeigen = offen && q.trim() !== "";
  return (
    <div className="tk-voll-suche" role="search">
      <Icon name="search" />
      <input ref={feld} type="search" value={q} placeholder={t("Einstellungen durchsuchen ( / )", "Search settings ( / )")}
        aria-label={t("Einstellungen durchsuchen", "Search settings")} role="combobox" aria-expanded={zeigen} aria-controls={listeId} aria-autocomplete="list"
        aria-activedescendant={zeigen && treffer[aktiv] ? `${listeId}-${treffer[aktiv]!.karte.key}` : undefined}
        onChange={(e) => { setQ(e.target.value); setAktiv(0); setOffen(true); }} onFocus={() => setOffen(true)}
        onBlur={() => window.setTimeout(() => setOffen(false), 150)} onKeyDown={tasten} />
      {zeigen && (
        <ul id={listeId} role="listbox" className="tk-voll-treffer" aria-label={t("Treffer", "Results")}>
          {treffer.length === 0 && <li className="tk-voll-leer" role="option" aria-selected={false} aria-disabled>{t("Keine Einstellung gefunden.", "No setting found.")}</li>}
          {treffer.map((x, i) => (
            <li key={x.karte.key} id={`${listeId}-${x.karte.key}`} role="option" aria-selected={i === aktiv} className={i === aktiv ? "aktiv" : undefined}
              onMouseDown={(e) => { e.preventDefault(); waehle(x); }} onMouseEnter={() => setAktiv(i)}>
              <strong>{x.karte.titel()}{sperre(x.karte) && <span className="pill tk-einst-etikett">{sperre(x.karte)!.text}</span>}</strong>
              <small>{gruppeTitel(x.karte.gruppe)}{x.grund ? ` · ${x.grund}` : ""}</small>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
